/** HTTP route paths and DO endpoint paths. No general route-path forbid-rule: a generic slash-path regex cannot separate app routes from DO endpoints, external API and filesystem paths, so it is FP-dominant. */

import { DO_ROUTES } from '@federa/const/durable-object-routes';
import type { SlashLedRouteTable } from '@federa/types/route-tables';

/** User DO endpoint paths for `buildDoUrl('USER', ...)`. The `/${string}` satisfies-pin is load-bearing: the value is concatenated onto a base URL, so a slash-less entry silently mangles the path instead of routing to the handler.
 *
 *  A MEMBER HERE IS A LIVE ENDPOINT, not a reservation. The UserDO route table matches on these values and the gateway
 *  forwards every `/user/<id>/*` subpath to it, so an entry with no producer publishes a hand-drivable endpoint. Three
 *  were cut on that basis 2026-08-15 -- `/sendMessage`, `/insertPropertyToUserFeed`, `/handleActivityOperation` -- each
 *  with zero callers in the tree; add one back only together with the caller that needs it. */
export const USER_DO_ENDPOINTS = {
	GET_ROLE: '/getRole',
	GET_USER_DETAILS: '/getUserDetails',
	CREATE_USER: '/createUser',
} as const satisfies SlashLedRouteTable;

/** NOTIFICATIONS_SERVICE worker endpoint paths -- DISTINCT TRANSPORT from `USER_DO_ENDPOINTS`: reached via `binding.fetch()` on the service binding, NOT via `buildDoUrl`. Keep new User-DO RPC paths in `USER_DO_ENDPOINTS`.
 *  The segments the DO handler ALSO routes (`BaseStateDO.getRouteEntries` via `DO_ROUTES`) are DERIVED from that canonical
 *  leaf, so a caller composing `/state` can never drift from the handler that answers it. */
export const NOTIFICATIONS_SERVICE_ENDPOINTS = {
	STATE: DO_ROUTES.STATE,
	NOTIFICATIONS: '/notifications',
	MARK_READ: DO_ROUTES.MARK_READ,
	MARK_ALL_READ: '/mark-all-read',
	WS: '/ws',
	SEND: DO_ROUTES.SEND,
} as const satisfies SlashLedRouteTable;

/** Public mount prefix for the per-user notifications WS bridge. Both runtime consumers derive from it plus `buildNotificationsUserWsPath`, so a drift cannot silently break the WS upgrade. */
const NOTIFICATIONS_PUBLIC_USER_WS_PREFIX = '/notifications/user';

/** @runtime-derived-ssot-helper -- composes `NOTIFICATIONS_PUBLIC_USER_WS_PREFIX` + `NOTIFICATIONS_SERVICE_ENDPOINTS.WS` into the client-dial path. */
export function buildNotificationsUserWsPath(userId: string): string {
	return `${NOTIFICATIONS_PUBLIC_USER_WS_PREFIX}/${encodeURIComponent(userId)}${NOTIFICATIONS_SERVICE_ENDPOINTS.WS}`;
}

/**
 * @runtime-derived-ssot-helper -- the RECOGNIZER half of {@link buildNotificationsUserWsPath}, composed from the same
 * two consts so the dialler and the matcher cannot drift. Capture group 1 is the (already URL-encoded) userId.
 *
 * Spelled here rather than at each host shell: BOTH `apps/federa` and `apps/admin-federa` mount this one bridge on the
 * same path, and each had built this regex for itself off the same two consts -- so a change to either const would
 * have had to land in two places, and the WS upgrade fails SILENTLY when it does not (the request reaches the worker,
 * the DO answers 101, and the browser aborts with no response recorded).
 */
export const NOTIFICATIONS_USER_WS_RE = new RegExp(`^${NOTIFICATIONS_PUBLIC_USER_WS_PREFIX}/([^/]+)${NOTIFICATIONS_SERVICE_ENDPOINTS.WS}$`);

/** Property Durable Object endpoint paths, used with `buildDoUrl('PROPERTY', ...)` and matched by the PropertyDO HTTP route table (`dispatchHttpRoute`). */
export const PROPERTY_DO_ENDPOINTS = {
	CREATE_PROPERTY: '/createProperty',
	GET_DETAILS: '/getDetails',
} as const satisfies SlashLedRouteTable;

export const PAGE_ROUTES = {
	/** The site root. It RENDERS the explore feed rather than redirecting to it -- `apps/federa/src/app/page.tsx` returns
	 * `<ExplorePage />` directly -- so the feed is reachable at two paths and anything keying on "is this the feed?"
	 * must accept both. `EXPLORE_FEED_ROUTES` below is that list; do not compare against `EXPLORE` alone. */
	HOME: '/',
	EXPLORE: '/explore',
	WISHLISTS: '/wishlists',
	MY_TRANSACTIONS: '/my-transactions',
	MY_OWNERSHIP: '/my-ownership',
	OFFERS: '/offers',
	PROFILE: '/profile',
	ASSISTANT: '/assistant',
	NOTIFICATIONS: '/notifications',
	AUTH_OTP: '/auth/otp',
	PAYMENTS: '/payments',
	PROPERTY: '/property',
	/** The invitation landing page (contacts phase 2). It RENDERS nothing durable: it reads {@link INVITE_QUERY_PARAM},
	 *  carries the token forward as the post-auth destination's own query, and leaves. */
	JOIN: '/join',
} as const satisfies SlashLedRouteTable;

/** The `/join` capability parameter -- the invitation token that decides sign-up attribution. Named here beside the
 *  route it belongs to, so the link builder (`buildInviteUrl`) and the page that reads it share one spelling. */
export const INVITE_QUERY_PARAM = 'invite';

/**
 * Every path that renders the explore feed.
 *
 * TWO, not one: the root route returns `<ExplorePage />` rather than redirecting, so a viewer on `/` is looking at the
 * same feed as one on `/explore` and no navigation ever moves them between the two. Any "am I on the feed?" test must
 * accept both -- an `=== PAGE_ROUTES.EXPLORE` comparison silently disables itself for everyone who never typed the
 * longer url, which is most people, since `/` is where the site lands.
 *
 * Live witness: `shouldTrackExploreRoute` compared against `EXPLORE` alone, so scroll restoration never armed on `/` --
 * the click marker early-returned and the snapshot stayed `pending: false` forever.
 */
export const EXPLORE_FEED_ROUTES: readonly string[] = [PAGE_ROUTES.HOME, PAGE_ROUTES.EXPLORE];

/** @runtime-derived-ssot-helper -- composes `PAGE_ROUTES.PROPERTY`. Build a property detail page path from a property ID. */
export function buildPropertyPath(propertyId: string): string {
	return `${PAGE_ROUTES.PROPERTY}/${propertyId}`;
}

export const AUTH_ROUTES = {
	LOGIN: PAGE_ROUTES.AUTH_OTP,
	DEFAULT_REDIRECT: PAGE_ROUTES.EXPLORE,
} as const satisfies SlashLedRouteTable;
